import { MessageListItem } from '../types';
import { i18n } from '@/plugins/i18n';
import { store } from '@/store';
import { UserState } from '@/store/user';
import { QueuedMessage } from './ChatFramework.types';
import { spawnSystemMessage } from '../lib';
import { MessageType } from '../enum';
import Api from '@/common/api';
import { transactionParamsSwitch, betsTypeSwitch } from '@/common/sb-transaction.helpers';
import { ExternalTransactionType, ExternalTransactionKind } from '../enum/ExternalTransactionType';
import { toArr } from './ChatFramework.utils';
import { ArticleAutocompleteState } from '@/store/articleAutocomplete/model';

// Sequence
// 1. Get user foreign id.
// Predictions
type Prediction = () => QueuedMessage;

const getState = () => (store.state as { user: UserState, articleAutoComplete: ArticleAutocompleteState });
const getTitleParam = () => ({ botName: getState()?.articleAutoComplete?.widgetTitle || 'Help Assistant' });

const greylisted: Prediction = async () => {
  const vuexState = getState();
  if (!vuexState.user.greyListed) return [];

  console.log('{Predictive options} Greylisted');

  return spawnSystemMessage({
    type: MessageType.PRE_SUPPORT_PREDICTION,
    message: i18n.t('module.suggestion.withdrawal_issue', getTitleParam()) as string,
    config: {
      connect: {
        liveChat: true,
        loginRequired: true,
        openRequest: true,
        voiceChat: true,
        showDescriptionBox: true,
        connect: true,
        transactionType: ExternalTransactionType.WITHDRAWALS_PENDING,
      },
    },
  });
};

const failedDeposit: Prediction = async () => {
  const params = transactionParamsSwitch.match(ExternalTransactionType.DEPOSITS_FAILED);
  try {
    const res = await Api.getTransactionHistory({
      ...params,
      pageSize: 5,
      createTimeStart: getHourAgoTimestamp(6),
      _t: +new Date(),
    });
    if (res.bizCode !== 10000) { throw new Error('Cannot get transactions'); }
    if (!res.data.statements.length) { return []; }

    console.log('{Predictive options} Failed Deposits');
    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      message: i18n.t('module.suggestion.deposit_issue', getTitleParam()) as string,
      config: {
        connect: {
          liveChat: true,
          loginRequired: true,
          openRequest: true,
          voiceChat: true,
          showDescriptionBox: true,
          connect: true,
          transactionType: ExternalTransactionType.DEPOSITS_FAILED,
        },
      },
    });
  } catch (e) {
    return [ /* Gracefully ignore the error */];
  }
};

const failedWithdrawals: Prediction = async () => {
  const params = transactionParamsSwitch.match(ExternalTransactionType.WITHDRAWALS_FAILED);
  try {
    const res = await Api.getTransactionHistory({
      ...params,
      pageSize: 5,
      createTimeStart: getHourAgoTimestamp(6),
      _t: +new Date(),
    });
    if (res.bizCode !== 10000) { throw new Error('Cannot get transactions'); }
    if (!res.data.statements.length) { return []; }
    console.log('{Predictive options} Failed Withdrawals');
    return spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PREDICTION,
      message: i18n.t('module.suggestion.withdrawal_issue', getTitleParam()) as string,
      config: {
        connect: {
          liveChat: true,
          loginRequired: true,
          openRequest: true,
          voiceChat: true,
          showDescriptionBox: true,
          connect: true,
          transactionType: ExternalTransactionType.WITHDRAWALS_FAILED,
        },
      },
    });
  } catch (e) {
    return [ /* Gracefully ignore the error */];
  }
};

const checkBetType = async (type: ExternalTransactionKind) => {
  const winningStatus = betsTypeSwitch.match(type);
  try {
    const res = await Api.getBetHistory({
      // add createdStart.
      winningStatus,
      pageSize: 5,
      startTime: getHourAgoTimestamp(6),
      _t: +new Date(),
    });
    if (res.bizCode !== 10000) { throw new Error('Cannot get transactions'); }
    return res.data.entityList;
  } catch (e) {
    return [];
  }
};

const lostBets: Prediction = async () => {
  const bets = await checkBetType(ExternalTransactionType.BETS_LOST);
  if (!bets.length) { return []; }
  console.log('{Predictive options} Lost Bets');

  return spawnSystemMessage({
    type: MessageType.PRE_SUPPORT_PREDICTION,
    message: i18n.t('module.suggestion.lost_bet_issue', getTitleParam()) as string,
    config: {
      connect: {
        liveChat: true,
        loginRequired: true,
        openRequest: true,
        voiceChat: true,
        showDescriptionBox: true,
        connect: true,
        transactionType: ExternalTransactionType.BETS_LOST,
      },
    },
  });
};

const unsettledBets: Prediction = async () => {
  const bets = await checkBetType(ExternalTransactionType.BETS_UNSETTLED);
  if (!bets.length) return [];
  console.log('{Predictive options} Unsettled Bets');

  return spawnSystemMessage({
    type: MessageType.PRE_SUPPORT_PREDICTION,
    message: i18n.t('module.suggestion.unsettled_bet_issue', getTitleParam()) as string,
    config: {
      connect: {
        liveChat: true,
        loginRequired: true,
        openRequest: true,
        voiceChat: true,
        showDescriptionBox: true,
        connect: true,
        transactionType: ExternalTransactionType.BETS_UNSETTLED,
      },
    },
  });
};

const voidedBets: Prediction = async () => {
  const bets = await checkBetType(ExternalTransactionType.BETS_VOIDED);
  if (!bets.length) return [];
  console.log('{Predictive options} Voided Bets');

  return spawnSystemMessage({
    type: MessageType.PRE_SUPPORT_PREDICTION,
    message: i18n.t('module.suggestion.voided_bet_issue', getTitleParam()) as string,
    config: {
      connect: {
        liveChat: true,
        loginRequired: true,
        openRequest: true,
        voiceChat: true,
        showDescriptionBox: true,
        connect: true,
        transactionType: ExternalTransactionType.BETS_VOIDED,
      },
    },
  });
};

export async function resolvePredictiveMessages () {
  const SEQUENCE = [
    [greylisted],
    [failedDeposit, failedWithdrawals],
    [lostBets, unsettledBets, voidedBets],
  ];

  for (const parallels of SEQUENCE) {
    const found = await Promise.all(parallels.map(x => Promise.resolve(x()))).then(r => r.find(res => toArr(res).length));
    if (found) return found as MessageListItem|MessageListItem[];
  }
  return [];
}

function getHourAgoTimestamp (hours = 6) {
  // Get the current time in milliseconds
  let currentTime = Date.now();

  // Subtract 6 hours (6 * 60 * 60 * 1000 milliseconds)
  let sixHoursInMillis = hours * 60 * 60 * 1000;
  let pastTimestamp = currentTime - sixHoursInMillis;
  return pastTimestamp;
}
